import { FC, ReactNode, useEffect, useState, useMemo, Suspense } from 'react';
import { PROFILE_AVATARS } from 'constants/index';

import { AppContext } from 'contexts/app';
import useLoggedInUser from 'utils/useLoggedInUser';
import { setUserInfo } from 'services/analytics';
import * as Sentry from '@sentry/react';
import { StatsigInitializer } from 'services/statsig/StatsigInitializer';
import { Redirect, useRouterActions } from 'react-resource-router';
import { getEnvironment, hasProfileConsented } from 'utils/index';
import { AtlassianAccountUserFragment$data } from '~graphql/__generated__/AtlassianAccountUserFragment.graphql';
import LoadingSpinner from '@atlassian/community-library/dist/components/loading-spinner';
import { ConsentWrapper } from './ConsentWrapper';
import { ErrorBoundary } from 'react-error-boundary';
import Fallback from 'components/common/fallback/Fallback';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { UnifiedConsentQuery } from 'graphql/__generated__/UnifiedConsentQuery.graphql';
import { UNIFIED_CONSENT_QUERY } from 'graphql/UnifiedConsentQuery';
import {
  CommunityInitializeMutation,
  CommunityInitializeMutation$data,
} from '../../graphql/__generated__/CommunityInitializeMutation.graphql';
import { COMMUNITY_INITIALIZE_MUTATION } from '../../graphql/CommunityInitializeMutation';
import { InitializedCommunityContext } from '../../contexts/initialized-community';

type CommunityInitializerProps = {
  children: ReactNode;
  loggedInUser: Partial<AtlassianAccountUserFragment$data> | null;
};
const CommunityInitializer: FC<CommunityInitializerProps> = ({
  children,
  loggedInUser,
}) => {
  const [isCommunityInitialized, setIsCommunityInitialized] = useState<
    boolean | null
  >(null);
  const [commit] = useMutation<CommunityInitializeMutation>(
    COMMUNITY_INITIALIZE_MUTATION,
  );
  const [profileInternalId, setProfileInternalId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (loggedInUser?.accountId) {
      (async () => {
        try {
          if (!loggedInUser.email || !loggedInUser.accountId) {
            throw new Error('User email or aaid is not available');
          }
          commit({
            variables: {
              aaid: loggedInUser.accountId,
            },
            onCompleted: (data: CommunityInitializeMutation$data) => {
              setIsCommunityInitialized(true);
              const profileInternalIdValue =
                data?.unified?.community?.initializeCommunity?.unifiedProfile
                  ?.internalId ?? '';
              setProfileInternalId(profileInternalIdValue);
            },
            onError: () => {
              setIsCommunityInitialized(false);
              Sentry.captureException(new Error('Error initialize community'), {
                data: {
                  message: 'Initialize community failied',
                  environment: getEnvironment(),
                  identifiers: {
                    accountId: loggedInUser.accountId,
                  },
                },
              });
            },
          });
        } catch (e) {
          setIsCommunityInitialized(false);
        }
      })();
    }
  }, [loggedInUser?.accountId]);

  if (!loggedInUser?.accountId) {
    return <>{children}</>;
  }
  if (isCommunityInitialized === null) {
    return <LoadingSpinner />;
  }
  if (!isCommunityInitialized) {
    return <Redirect to="/user/error/invalid_path" />;
  }
  return (
    <InitializedCommunityContext.Provider
      value={{
        profileInternalId,
      }}
    >
      {children}
    </InitializedCommunityContext.Provider>
  );
};

type InitializerProps = {
  children: ReactNode;
};

export const { REACT_APP_ACCOUNT_LINK_BASE_URL } = process.env;

export const Initializer: FC<InitializerProps> = (props) => {
  // a top level component to handle any initialization logic
  if (
    getEnvironment() !== 'local' &&
    window.location.origin === REACT_APP_ACCOUNT_LINK_BASE_URL &&
    !window.location.pathname.includes('/authenticate/') // update this logic to match a more accurate path.
  ) {
    return <Redirect to="/error/invalid_path" />;
  }

  useEffect(() => {
    Sentry.init({
      dsn: 'https://5fa66ef9a8ff88865fbd3d073ebe4dfa@o55978.ingest.us.sentry.io/4507855280209920',
      enabled:
        process.env.REACT_APP_ENVI !== 'local' &&
        process.env.REACT_APP_ENVI !== 'development',
      environment: process.env.REACT_APP_ENVI,
      integrations: [Sentry.browserTracingIntegration()],
      // Tracing
      release: process.env.SENTRY_RELEASE_TAG ?? 'local',
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'localhost',
        process.env.REACT_APP_UPP_GRAPHQL_URL || '',
      ],
    });
  }, []);

  const profileIconPosition = useMemo(
    () => Math.floor(Math.random() * 100) % PROFILE_AVATARS.length,
    [],
  );

  const user = useLoggedInUser() || {};
  useEffect(() => {
    if (user?.accountId) {
      setUserInfo(user.accountId);
    }
  }, [user?.accountId]);

  const profileConsentData = useLazyLoadQuery<UnifiedConsentQuery>(
    UNIFIED_CONSENT_QUERY,
    { aaid: user?.accountId ?? '' },
    { fetchPolicy: 'store-or-network' },
  );

  const profileHasConsented =
    getEnvironment() !== 'local'
      ? useMemo(
          () =>
            hasProfileConsented(
              profileConsentData.unified?.consent?.getConsent?.consentObj ?? [],
            ),
          [profileConsentData],
        )
      : true;

  const { push } = useRouterActions();

  const onConsentError = () => {
    push('/user/error/consent_failed');
  };

  const isErrorPage = window.location.pathname.includes('/error/');

  return !isErrorPage ? (
    <ErrorBoundary onError={onConsentError} FallbackComponent={Fallback}>
      <Suspense fallback={<LoadingSpinner />}>
        <ConsentWrapper profileHasConsented={profileHasConsented}>
          <CommunityInitializer loggedInUser={user}>
            <AppContext.Provider
              value={{
                profileAvatar: PROFILE_AVATARS[profileIconPosition],
              }}
            >
              <StatsigInitializer>{props.children}</StatsigInitializer>
            </AppContext.Provider>
          </CommunityInitializer>
        </ConsentWrapper>
      </Suspense>
    </ErrorBoundary>
  ) : (
    <AppContext.Provider
      value={{
        profileAvatar: PROFILE_AVATARS[profileIconPosition],
      }}
    >
      <StatsigInitializer>{props.children}</StatsigInitializer>
    </AppContext.Provider>
  );
};
